import { submissionApiClient } from "@/api/submission-api";
import { workspaceApi, type Workspace } from "@/api/workspace-api";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/auth-store";
import { isAxiosError } from "axios";
import { setPostHogGroup } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router";

import { ApplicationDeadlineCard } from "@/components/portal/application-deadline-card";
import { EligibilityCriteriaCard } from "@/components/portal/eligibility-criteria-card";
import { DescriptionCard } from "@/components/portal/description-card";
import { InvestmentDetailsCard } from "@/components/portal/investment-details-card";
import { InvestmentTeamCard } from "@/components/portal/investment-team-card";

export default function Portal() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const id = searchParams.get("id");
  const [data, setData] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<{
    hasApplied: boolean;
    applicationStatus: string | null;
    applicationId: string | null;
  } | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);


  useEffect(() => {
    const fetchOpportunity = async () => {
      if (!slug && !id) {
        setError("No opportunity ID or slug provided");
        setLoading(false);
        return;
      }

      try {
        const response = slug
          ? await workspaceApi.getPortalWorkspaceBySlug(slug)
          : await workspaceApi.getPortalWorkspace(id!);
        setData(response.result);

        if (response.result?._id) {
          setPostHogGroup("workspace", response.result._id, {
            name: response.result.opportunity_name,
          });
        }

        if (id && !slug && response.result.portal_slug) {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.delete("id");
          const queryString = currentParams.toString();
          const redirectUrl = `/p/${response.result.portal_slug}${queryString ? `?${queryString}` : ""}`;
          window.location.replace(redirectUrl);
          return;
        }

        const workspaceId = response.result._id;
        if (user && user.id && workspaceId) {
          try {
            const statusResponse =
              await submissionApiClient.application.checkApplicationStatus(
                workspaceId,
                user.id
              );
            setApplicationStatus({
              hasApplied: statusResponse.hasApplied,
              applicationStatus: statusResponse.applicationStatus,
              applicationId: statusResponse.applicationId,
            });
          } catch (err) {
            console.error("Error checking application status:", err);
            setApplicationStatus({
              hasApplied: false,
              applicationStatus: null,
              applicationId: null,
            });
          }
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError("Failed to load opportunity data");
        }
        console.error("Error fetching opportunity:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunity();
  }, [id, slug, user, navigate]);

  const getCompanyAvatar = () => {
    if (!data?.affiliated_company?.files) return null;
    const avatarFile = data.affiliated_company.files.find(
      (file) => file.file_type === "company avatar"
    );
    return avatarFile?.file_url || null;
  };

  const getButtonText = () => {
    if (!applicationStatus || !applicationStatus.hasApplied) return "Apply";
    if (applicationStatus.applicationStatus === "Portfolio Companies") return "Update";
    return "Edit";
  };

  const getButtonAction = () => {
    const workspaceId = data?._id || id;
    if (!applicationStatus || !applicationStatus.hasApplied) {
      return () => {
        if (slug) navigate(`/p/${slug}/submission`);
        else navigate(`/portal/submission?id=${workspaceId}`);
      };
    }
    return () => {
      if (slug) navigate(`/p/${slug}/submission?edit=true`);
      else navigate(`/portal/submission?id=${workspaceId}&edit=true`);
    };
  };

  if (loading) {
    return (
      <div className="bg-white min-h-screen w-full flex items-center justify-center font-geist-sans">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4" />
          <p className="text-gray-600">Loading opportunity...</p>
        </div>
      </div>
    );
  }

  if (notFound || error || !data) {
    return (
      <div className="bg-white min-h-screen w-full flex items-center justify-center font-geist-sans">
        <div className="max-w-md w-full px-6 py-8 text-center">
          <h1 className="text-8xl font-bold text-gray-200 mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
          <p className="text-gray-500 mb-6">
            This application portal couldn't be found. Please verify the link and try again.
          </p>
          <a href="https://dgpt.io">
            <Button>Go to Homepage</Button>
          </a>
        </div>
      </div>
    );
  }

  const primaryLocation =
    data.geographical_location && data.geographical_location.length > 0
      ? data.geographical_location[0]
      : undefined;

  return (
    <div className="h-screen bg-background font-geist-sans selection:bg-black selection:text-white">

      {/* ── Scroll container ──────────────────────────────────────────── */}
      <div
        ref={contentRef}
        className="portal-content-scroll fixed bottom-0 left-0 right-0 top-0 overflow-y-auto"
        style={{ scrollbarWidth: "none", overscrollBehavior: "contain" }}
        onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 20)}
      >
        <style>{`.portal-content-scroll::-webkit-scrollbar { display: none; }`}</style>

        {/* ── Collapsible Fixed Header ───────────────────────────────── */}
        <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/90 backdrop-blur-md transition-all duration-300">
          <div className="mx-auto max-w-6xl px-4 md:px-6 lg:px-8">
            <div
              className={cn(
                "flex justify-between overflow-hidden transition-all duration-300",
                isScrolled
                  ? "h-14 items-center gap-4"
                  : "items-start gap-6 pt-10 pb-10 md:pt-[60px] md:pb-[75px]"
              )}
            >
              <div
                className={cn(
                  "flex min-w-0 transition-all duration-300",
                  isScrolled
                    ? "flex-row items-center gap-3"
                    : "flex-col items-start gap-1"
                )}
              >
                {getCompanyAvatar() && !imageError && (
                  <img
                    src={getCompanyAvatar()!}
                    alt="Company Logo"
                    className={cn(
                      "rounded object-contain transition-all duration-300",
                      isScrolled
                        ? "h-7 w-auto shrink-0"
                        : "mb-2 h-12 w-auto max-w-[180px] md:h-14"
                    )}
                    onError={() => setImageError(true)}
                  />
                )}
                <div className="flex flex-col min-w-0">
                  <h1
                    className={cn(
                      "font-bold tracking-tight transition-all duration-300 text-foreground",
                      isScrolled
                        ? "truncate text-lg"
                        : "mb-1 text-2xl sm:text-3xl md:text-4xl"
                    )}
                  >
                    {data.opportunity_name}
                  </h1>
                  <div
                    className={cn(
                      "transition-all duration-300 overflow-hidden",
                      isScrolled ? "max-h-0 opacity-0" : "max-h-24 opacity-100"
                    )}
                  >
                    <div className="text-sm text-muted-foreground">
                      {data.opportunity_type}
                      {primaryLocation && (
                        <>
                          <span className="mx-2">•</span>
                          {primaryLocation}
                        </>
                      )}
                    </div>
                    {data.website_url && (
                      <a
                        href={data.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block text-xs text-muted-foreground underline-offset-4 hover:underline"
                      >
                        {data.website_url.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <Button
                size={isScrolled ? "default" : "lg"}
                className={cn(
                  "shrink-0 transition-all duration-300",
                  isScrolled ? "px-5" : "mt-1 px-6"
                )}
                onClick={getButtonAction()}
              >
                {getButtonText()}
              </Button>
            </div>
          </div>
        </header>

        {/* ── Invisible Spacer to prevent scroll layout jump ────────── */}
        <div className="pointer-events-none invisible opacity-0" aria-hidden>
          <div className="mx-auto max-w-6xl px-4 pt-10 pb-10 md:px-6 lg:px-8 md:pt-[60px] md:pb-[75px]">
            <div className="flex items-start justify-between gap-6">
              <div className="flex flex-col items-start gap-1 min-w-0">
                {getCompanyAvatar() && !imageError && (
                  <img
                    src={getCompanyAvatar()!}
                    alt="Company Logo"
                    className="mb-2 h-12 w-auto max-w-[180px] md:h-14"
                    onError={() => setImageError(true)}
                  />
                )}
                <div className="flex flex-col min-w-0">
                  <h1 className="mb-1 text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
                    {data.opportunity_name}
                  </h1>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      {data.opportunity_type}
                      {primaryLocation && (
                        <>
                          <span className="mx-2">•</span>
                          {primaryLocation}
                        </>
                      )}
                    </div>
                    {data.website_url && (
                      <a href="#" className="mt-1 block text-xs">
                        {data.website_url}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <Button size="lg" className="mt-1 shrink-0 px-6">
                {getButtonText()}
              </Button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="mx-auto flex w-full max-w-6xl gap-8 px-4 py-8 md:px-6 lg:px-8">
          <main className="min-w-0 flex-1">
            <section className="mb-8">
              <DescriptionCard description={data.opportunity_description} />
            </section>

            <div className="mb-8 flex flex-col gap-6 lg:hidden">
              <ApplicationDeadlineCard
                startDate={data.start_intake_date}
                endDate={data.end_intake_date}
              />
              <EligibilityCriteriaCard
                gicsIndustries={data.target_gics_industries}
                gicsSubIndustries={data.target_gics_sub_industries}
                revenueModels={data.target_revenue_models}
                stages={data.target_stage_companies}
                locations={data.geographical_location}
                businessModels={data.target_model_companies}
                distinctions={data.target_distinctions}
              />
            </div>

            <div className="mb-8">
              <InvestmentDetailsCard
                minChequeSize={data.min_cheque_size}
                maxChequeSize={data.max_cheque_size}
                currency={data.currency}
                prize={data.prize}
              />
            </div>

            {data.show_judge_on_opportunity && (
              <div className="mb-8">
                <InvestmentTeamCard teamMembers={data.team_members} />
              </div>
            )}
          </main>

          <aside className="hidden w-80 shrink-0 lg:block">
            <div className="sticky top-0 flex flex-col gap-6">
              <ApplicationDeadlineCard
                startDate={data.start_intake_date}
                endDate={data.end_intake_date}
              />
              <EligibilityCriteriaCard
                gicsIndustries={data.target_gics_industries}
                gicsSubIndustries={data.target_gics_sub_industries}
                revenueModels={data.target_revenue_models}
                stages={data.target_stage_companies}
                locations={data.geographical_location}
                businessModels={data.target_model_companies}
                distinctions={data.target_distinctions}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
